/* ==========================================================================
   CART-02 — Cart Drawer behaviour
   Source of truth: Figma "CART-02 — CART DRAWER — CURRENT APPROVED"

   Additive module. It does NOT modify Ella's cart pipeline:
     - mutations still go through cart.js (/cart/change + Section Rendering)
     - the coupon still goes through cart-discount-component (/cart/update.js)
     - add-to-cart still goes through product-form.js (/cart/add.js)

   It owns only what CART-02 adds on top:
     header count · free-shipping bar · footer totals/note · localized loading
     · coupon panel · shipping sheet · recommendation refill · error surfaces
     · Undo.

   Everything is mounted on .drawer__inner or re-applied after each rerender,
   because cart.js#updateSingleSection replaces .section--main-cart wholesale.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER_SEL = '#CartDrawer';
  var UNDO_MS = 5000;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function drawer() { return $(DRAWER_SEL); }
  function t(key, fallback) {
    var s = window.cart02Strings || {};
    return (s[key] === undefined || s[key] === null) ? (fallback || '') : s[key];
  }
  function fill(str, map) {
    // Shopify resolves locale placeholders to the literal tokens we pass in
    // Liquid ([title], [quantity]); any unresolved {{ var }} is handled too.
    return String(str)
      .replace(/\[(\w+)\]/g, function (m, k) { return map[k] === undefined ? m : map[k]; })
      .replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, k) { return map[k] === undefined ? m : map[k]; });
  }
  function money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.cart02MoneyFormat) {
      try { return window.Shopify.formatMoney(cents, window.cart02MoneyFormat); } catch (e) { /* fall through */ }
    }
    return (Math.round(cents) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  function cfg(type) {
    if (typeof window.fetchConfig === 'function') return window.fetchConfig(type || 'json');
    return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
  }
  function sectionId() {
    var el = $('cart-drawer-items');
    return el ? el.dataset.sectionId : null;
  }
  function itemsComponent() {
    return $('cart-drawer-items') || $('cart-items-component');
  }
  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function announce(msg) {
    var live = $('#CartDrawer-LiveRegionText');
    if (live) live.textContent = msg;
  }

  /* ---------------------------------------------------------------- state */

  var lastCart = null;       // most recent cart JSON we have seen
  var undoState = null;      // { snapshot, timer, deadline, paused }
  var pendingRemoval = null; // { variantId, key, at }

  /* ------------------------------------------------- 1. header + footer */

  function syncCount(cart) {
    var d = drawer(); if (!d || !cart) return;
    var n = cart.item_count;
    $$('[data-c2-count]', d).forEach(function (el) { el.textContent = n; });
  }

  function syncFooter(cart, src) {
    var d = drawer(); if (!d || !cart) return;

    var totalEl = $('[data-c2-total-value]', d);
    if (totalEl) {
      var totalText = money(cart.total_price);
      if (totalEl.textContent !== totalText) totalEl.textContent = totalText;
    }

    // discount line — only when a cart-level discount is actually applied
    var discount = 0;
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      discount += (a.total_allocated_amount || 0);
    });
    var line = $('[data-c2-discount-line]', d);
    if (line) {
      if (discount > 0) {
        line.hidden = false;
        var b = line.querySelector('b');
        if (b) {
          var discountText = '− ' + money(discount);
          if (b.textContent !== discountText) b.textContent = discountText;
        }
      } else {
        line.hidden = true;
      }
    }
    syncFreeShipping(cart);
  }

  /* ------------------------------------------- 2. free shipping (45px) */

  function threshold() {
    var d = drawer();
    var v = d && d.dataset.c2Threshold ? parseInt(d.dataset.c2Threshold, 10) : NaN;
    if (!isNaN(v) && v > 0) return v * 100;                 // setting is in major units
    if (window.free_shipping_price) return parseInt(window.free_shipping_price, 10) * 100;
    return 0;
  }

  function syncFreeShipping(cart) {
    var d = drawer(); if (!d || !cart) return;
    var wrap = $('[data-c2-fs]', d); if (!wrap) return;
    var limit = threshold(); if (!limit) return;

    // CART-02: progress is computed from the cart value AFTER discounts.
    var value = cart.total_price;
    var remaining = limit - value;
    var achieved = remaining <= 0;
    var pct = Math.max(0, Math.min(100, Math.round((value / limit) * 100)));

    wrap.classList.toggle('c2-achieved', achieved);
    var msg = $('[data-shipping-message]', wrap);
    if (msg) {
      if (achieved) {
        msg.textContent = t('fsAchieved');
      } else {
        msg.innerHTML = '';
        msg.appendChild(document.createTextNode(t('fsBefore') + ' '));
        var b = document.createElement('b');
        b.className = 'c2-num';
        b.textContent = money(remaining);
        msg.appendChild(b);
        msg.appendChild(document.createTextNode(' ' + t('fsAfter')));
      }
    }
    var meter = $('[data-free-shipping-progress-meter]', wrap);
    if (meter) meter.style.width = pct + '%';
    var bar = $('.progress_shipping', wrap);
    if (bar) bar.setAttribute('aria-valuenow', String(pct));

    var note = $('[data-c2-note]', d);
    if (note) {
      var noteText = achieved ? t('noteFree') : t('note');
      if (note.textContent !== noteText) note.textContent = noteText;
    }
  }

  /* --------------------------------------- 3. row state + localized load */

  function syncRows() {
    var d = drawer(); if (!d) return;
    $$('.cart-item', d).forEach(function (row) {
      var input = row.querySelector('.quantity__input');
      var minus = row.querySelector('.quantity-minus');
      if (!input || !minus) return;
      var q = parseInt(input.value, 10) || 1;
      // CART-02: quantity never reaches 0 — removal is only the trash icon.
      var atMin = q <= 1;
      minus.classList.toggle('c2-min', atMin);
      minus.setAttribute('aria-disabled', atMin ? 'true' : 'false');
      input.setAttribute('min', '1');
      var inc = row.querySelector('.quantity-plus');
      var title = row.dataset.c2Title || '';
      if (inc) inc.setAttribute('aria-label', fill(t('a11yIncrease'), {}) + ' ' + title);
      minus.setAttribute('aria-label', fill(t('a11yDecrease'), {}) + ' ' + title);
      var rm = row.querySelector('.c2-remove');
      if (rm) rm.setAttribute('aria-label', fill(t('a11yRemove'), { title: title }));
    });
  }

  function markRowLoading(row, on) {
    if (!row) return;
    row.classList.toggle('c2-loading', !!on);
    var input = row.querySelector('.quantity__input');
    if (!input) return;
    var slot = row.querySelector('.c2-qty-spinner');
    if (on) {
      if (!slot) {
        slot = document.createElement('span');
        slot.className = 'c2-spinner c2-qty-spinner';
        input.style.display = 'none';
        input.parentNode.insertBefore(slot, input);
      }
    } else if (slot) {
      slot.parentNode.removeChild(slot);
      input.style.display = '';
    }
  }

  function markTotalUpdating(on) {
    var el = $('[data-c2-total]', drawer());
    if (el) el.classList.toggle('c2-updating', !!on);
  }

  /* --------------------------------------------------- 4. error surfaces */

  function showBanner(message) {
    var b = $('[data-c2-banner]', drawer()); if (!b) return;
    var m = $('[data-c2-banner-msg]', b); if (m && message) m.textContent = message;
    b.hidden = false;
    var co = $('#CartDrawer-Checkout');
    if (co) { co.classList.add('c2-paused'); co.setAttribute('aria-disabled', 'true'); }
  }
  function hideBanner() {
    var b = $('[data-c2-banner]', drawer()); if (!b) return;
    b.hidden = true;
    var co = $('#CartDrawer-Checkout');
    if (co) { co.classList.remove('c2-paused'); co.removeAttribute('aria-disabled'); }
  }

  function showRowError(row, message) {
    if (!row) return;
    clearRowError(row);
    var box = document.createElement('div');
    box.className = 'c2-row-error';
    box.setAttribute('role', 'alert');
    var span = document.createElement('span');
    span.textContent = message;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = t('errQtyRetry');
    btn.addEventListener('click', function () {
      clearRowError(row);
      var plus = row.querySelector('.quantity-plus');
      if (plus) plus.click();
    });
    box.appendChild(span); box.appendChild(btn);
    row.parentNode.insertBefore(box, row.nextSibling);
  }
  function clearRowError(row) {
    if (!row) return;
    var next = row.nextElementSibling;
    if (next && next.classList.contains('c2-row-error')) next.parentNode.removeChild(next);
  }

  /* ------------------------------------------------------- 5. undo toast */

  function toastEl() { return $('[data-c2-toast]', drawer()); }

  function hideToast() {
    var el = toastEl(); if (!el) return;
    el.hidden = true;
    el.classList.remove('is-open', 'is-busy', 'is-error');
    if (undoState && undoState.timer) clearTimeout(undoState.timer);
    undoState = null;
  }

  function startTimer() {
    if (!undoState) return;
    if (undoState.timer) clearTimeout(undoState.timer);
    undoState.deadline = Date.now() + undoState.remaining;
    undoState.timer = setTimeout(hideToast, undoState.remaining);
  }
  function pauseTimer() {
    if (!undoState || !undoState.timer) return;
    clearTimeout(undoState.timer);
    undoState.timer = null;
    undoState.remaining = Math.max(400, undoState.deadline - Date.now());
  }
  function resumeTimer() {
    if (!undoState || undoState.timer) return;
    startTimer();
  }

  function showToast(opts) {
    var el = toastEl(); if (!el) return;
    var msg = $('[data-c2-toast-msg]', el);
    var act = $('[data-c2-toast-action]', el);
    el.classList.toggle('is-error', !!opts.error);
    el.classList.remove('is-busy');
    if (msg) msg.textContent = opts.message;
    if (act) {
      if (opts.action) { act.hidden = false; act.textContent = opts.action; }
      else { act.hidden = true; act.textContent = ''; }
    }
    el.hidden = false;
    el.classList.add('is-open');
    announce(opts.message);

    undoState = Object.assign({}, undoState, {
      snapshot: opts.snapshot !== undefined ? opts.snapshot : (undoState && undoState.snapshot),
      remaining: UNDO_MS,
      onAction: opts.onAction || null
    });
    startTimer();
  }

  function snapshotFromRow(row) {
    if (!row) return null;
    var props = {};
    try { props = JSON.parse(row.dataset.c2Properties || '{}'); } catch (e) { props = {}; }
    return {
      id: row.dataset.c2Variant,
      quantity: parseInt(row.dataset.c2Qty, 10) || 1,
      properties: props,
      sellingPlan: row.dataset.c2SellingPlan || null,
      title: row.dataset.c2Title || '',
      systemLine: row.dataset.c2SystemLine === 'true'
    };
  }

  function restore() {
    if (!undoState || !undoState.snapshot) return;
    var snap = undoState.snapshot;
    var el = toastEl();
    var act = el && $('[data-c2-toast-action]', el);
    if (el) el.classList.add('is-busy');
    if (act) act.textContent = t('undoRestoring');
    if (undoState.timer) { clearTimeout(undoState.timer); undoState.timer = null; }

    var item = { id: snap.id, quantity: snap.quantity };
    if (snap.properties && Object.keys(snap.properties).length) item.properties = snap.properties;
    if (snap.sellingPlan) item.selling_plan = snap.sellingPlan;

    var body = { items: [item] };
    var sid = sectionId();
    if (sid) { body.sections = [sid]; body.sections_url = window.location.pathname; }

    fetch(window.routes.cart_add_url, Object.assign({}, cfg('json'), { body: JSON.stringify(body) }))
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, json: j }; }); })
      .then(function (res) {
        if (!res.ok || res.json.status) {
          var soldOut = res.status === 422;
          showToast({
            message: soldOut ? t('undoFailedSoldOut') : t('undoFailed'),
            action: soldOut ? null : t('undoRetry'),
            error: true,
            snapshot: soldOut ? null : snap,
            onAction: soldOut ? null : restore
          });
          return;
        }
        var data = res.json;
        if (data.sections) {
          var comp = itemsComponent();
          if (comp && typeof comp.updateSections === 'function') comp.updateSections(data.sections);
        }
        hideToast();
        if (typeof window.updateCartEmptyStateVisibility === 'function' && data.item_count !== undefined) {
          window.updateCartEmptyStateVisibility(data.item_count);
        }
        // one event only — publishing both would double-trigger every subscriber
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-02-undo', cartData: data });
        }
        afterCartUpdate(data, true, 'undo-restore');
        announce(t('undoRestored'));
        highlightRestored(snap.id);
      })
      .catch(function () {
        showToast({
          message: t('undoFailed'), action: t('undoRetry'), error: true,
          snapshot: snap, onAction: restore
        });
      });
  }

  function highlightRestored(variantId) {
    var d = drawer(); if (!d) return;
    var rows = $$('.cart-item', d).filter(function (r) { return r.dataset.c2Variant === String(variantId); });
    var row = rows[rows.length - 1];   // restored line is appended at the END
    if (!row) return;
    row.classList.add('c2-restored');
    if (reducedMotion()) return;
    setTimeout(function () { row.classList.remove('c2-restored'); }, 1500);
  }

  /* --------------------------------------------------- 6. remove + undo */

  function interceptRemove(e) {
    var btn = e.target.closest && e.target.closest('.cart-item__remove button, .c2-remove');
    if (!btn) return;
    var d = drawer(); if (!d || !d.contains(btn)) return;
    var row = btn.closest('.cart-item'); if (!row) return;

    var snap = snapshotFromRow(row);
    pendingRemoval = { variantId: snap && snap.id, at: Date.now(), snapshot: snap };
    // if no cart response arrives, drop the pending state rather than let a later
    // unrelated update surface a stale toast
    if (pendingRemoval.guard) clearTimeout(pendingRemoval.guard);
    pendingRemoval.guard = setTimeout(function () { pendingRemoval = null; }, 15000);
    clearRowError(row);
    hideToast();                    // a new removal replaces the previous Undo
    // The visual state is applied AFTER this click finishes: c2-removing sets
    // pointer-events:none, and setting it now would cancel Ella's own handler.
    setTimeout(function () {
      if (row.isConnected) { row.classList.add('c2-removing'); markTotalUpdating(true); }
    }, 0);
  }

  function afterRemoval(cart) {
    if (!pendingRemoval) return;
    var snap = pendingRemoval.snapshot;
    var stillThere = (cart.items || []).some(function (i) { return String(i.variant_id) === String(pendingRemoval.variantId); });
    var wasMultiple = snap && snap.quantity > 1;
    if (pendingRemoval.guard) clearTimeout(pendingRemoval.guard);
    pendingRemoval = null;

    if (stillThere && !wasMultiple) {
      // the removal did not take effect — restore the row and say so (fixes the
      // known Ella path where the DOM row is dropped before the response)
      renderSection();
      showToast({ message: t('errRemove'), action: t('errQtyRetry'), error: true, snapshot: null });
      return;
    }
    if (!snap) return;
    if (snap.systemLine) {
      // free gift / automatic-discount / bundle line: confirmation only, never undoable
      showToast({ message: t('undoRemovedGeneric'), action: null, snapshot: null });
      return;
    }
    showToast({
      message: fill(t('undoRemoved'), { title: snap.title }),
      action: t('undoAction'),
      snapshot: snap,
      onAction: restore
    });
  }

  function renderSection() {
    var comp = itemsComponent();
    var sid = sectionId();
    if (comp && sid && typeof comp.renderSection === 'function') comp.renderSection(sid);
  }

  /* ------------------------------------------------------- 7. coupon UI */

  function bindCoupon() {
    var d = drawer(); if (!d) return;
    // The panel lives inside <cart-drawer-items> and is destroyed by every
    // section replacement, while #CartDrawer — and this listener — survive.
    // Resolve it at click time; never hold it in the closure.
    d.addEventListener('click', function (e) {
      if (e.target.closest('[data-c2-coupon-open]')) {
        var panel = $('[data-c2-coupon]', d);
        if (!panel) return;
        panel.classList.add('is-open');
        var f = panel.querySelector('.cart-discount__input');
        if (f) setTimeout(function () { f.focus(); }, 30);
      } else if (e.target.closest('[data-c2-coupon-close]')) {
        var open = $('[data-c2-coupon]', d);
        if (open) open.classList.remove('is-open');
      }
    });
  }

  /* ----------------------------------------------- 8. shipping sheet */

  // Egypt's governorates, verbatim from Shopify's own country/province data:
  // [0] is the province string /cart/shipping_rates.json expects, [1] is the
  // label. Egypt-only store, so the worldwide country payload is not rendered
  // and nothing is parsed out of the DOM at boot.
  var EG_PROVINCES = [
    ["6th of October", "6th of October"],
    ["Al Sharqia", "الشرقية"],
    ["Alexandria", "الإسكندرية"],
    ["Aswan", "محافظة أسوان"],
    ["Asyut", "أسيوط"],
    ["Beheira", "محافظة البحيرة"],
    ["Beni Suef", "بني سويف"],
    ["Cairo", "محافظة القاهرة"],
    ["Dakahlia", "الدقهلية"],
    ["Damietta", "محافظة دمياط"],
    ["Faiyum", "محافظة الفيوم"],
    ["Gharbia", "الغربية"],
    ["Giza", "محافظة الجيزة"],
    ["Helwan", "Helwan"],
    ["Ismailia", "الإسماعيلية"],
    ["Kafr el-Sheikh", "محافظة كفر الشيخ"],
    ["Luxor", "محافظة الأقصر"],
    ["Matrouh", "محافظة مطروح"],
    ["Minya", "المنيا"],
    ["Monufia", "محافظة المنوفية"],
    ["New Valley", "محافظة الوادي الجديد"],
    ["North Sinai", "شمال سيناء"],
    ["Port Said", "بورسعيد"],
    ["Qalyubia", "محافظة القليوبية"],
    ["Qena", "محافظة قنا"],
    ["Red Sea", "البحر الأحمر"],
    ["Sohag", "محافظة سوهاج"],
    ["South Sinai", "جنوب سيناء"],
    ["Suez", "السويس"]
  ];

  function egyptProvinces() { return EG_PROVINCES; }

  function bindShipping() {
    var d = drawer(); if (!d) return;
    var sheet = $('[data-c2-sheet]', d);
    var select = $('[data-c2-governorate]', d);
    if (!sheet || !select) return;

    // Egypt is fixed: only the governorate is chosen, and the list is real
    // Shopify province data — nothing is invented and no postcode is asked for.
    // Filled on first open, never during page boot, and only once.
    var provincesFilled = false;
    function fillProvinces() {
      if (provincesFilled) return;
      provincesFilled = true;
      if (select.options.length > 1) return;
      egyptProvinces().forEach(function (p) {
        var o = document.createElement('option');
        o.value = p[0]; o.textContent = p[1];
        select.appendChild(o);
      });
    }

    function open() { fillProvinces(); d.classList.add('c2-sheet-open'); sheet.focus(); }
    function close() { d.classList.remove('c2-sheet-open'); sheet.classList.remove('is-required'); }

    d.addEventListener('click', function (e) {
      if (e.target.closest('[data-c2-shipping-open]')) open();
      else if (e.target.closest('[data-c2-sheet-close]') || e.target.closest('[data-c2-sheet-scrim]')) close();
    });
    d.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && d.classList.contains('c2-sheet-open')) close();
    });

    var calc = $('[data-c2-calc]', d);
    var label = $('[data-c2-calc-label]', d);
    var result = $('[data-c2-sheet-result]', d);

    calc.addEventListener('click', function () {
      if (!select.value) { sheet.classList.add('is-required'); select.focus(); return; }
      sheet.classList.remove('is-required');
      calc.setAttribute('aria-busy', 'true');
      if (label) label.textContent = t('shippingCalculating');

      var url = window.routes.cart_url + '/shipping_rates.json'
        + '?shipping_address[country]=Egypt'
        + '&shipping_address[province]=' + encodeURIComponent(select.value)
        + '&shipping_address[zip]=';
      fetch(url.replace(/\/\//g, '/').replace(':/', '://'), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          result.hidden = false;
          result.className = 'c2-sheet__result';
          var rates = res.json && res.json.shipping_rates;
          if (res.ok && rates && rates.length) {
            var best = rates[0];
            var gov = select.options[select.selectedIndex].textContent;
            result.innerHTML = '';
            result.appendChild(document.createTextNode(gov + ' · ' + best.name + ' — '));
            var n = document.createElement('span');
            n.className = 'c2-num';
            n.textContent = money(parseFloat(best.price) * 100);
            result.appendChild(n);
            if (label) label.textContent = t('shippingChange');
          } else if (res.ok) {
            result.className = 'c2-sheet__result c2-sheet__result--none';
            result.textContent = t('shippingNone') + ' · ' + t('shippingNoneHint');
            if (label) label.textContent = t('shippingCalculate');
          } else {
            result.className = 'c2-sheet__result c2-sheet__result--err';
            result.textContent = t('shippingError');
            if (label) label.textContent = t('shippingCalculate');
          }
        })
        .catch(function () {
          result.hidden = false;
          result.className = 'c2-sheet__result c2-sheet__result--err';
          result.textContent = t('shippingError');
          if (label) label.textContent = t('shippingCalculate');
        })
        .then(function () { calc.removeAttribute('aria-busy'); });
    });
  }

  /* ------------------------------------------- 9. recommendations refill */

  function bindRecommendations() {
    var d = drawer(); if (!d) return;
    d.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-c2-rec-add]');
      if (!btn) return;
      e.preventDefault();
      var row = btn.closest('.c2-rec');
      var id = btn.getAttribute('data-c2-rec-add');
      if (!id || btn.getAttribute('aria-busy') === 'true') return;

      btn.setAttribute('aria-busy', 'true');
      var original = btn.innerHTML;
      btn.innerHTML = '<span class="c2-spinner"></span>';

      var body = { items: [{ id: id, quantity: 1 }] };
      var sid = sectionId();
      if (sid) { body.sections = [sid]; body.sections_url = window.location.pathname; }

      fetch(window.routes.cart_add_url, Object.assign({}, cfg('json'), { body: JSON.stringify(body) }))
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok || res.json.status) throw new Error('add failed');
          // adding is a cart mutation: it commits any pending Undo
          hideToast();
          if (res.json.sections) {
            var comp = itemsComponent();
            if (comp && typeof comp.updateSections === 'function') comp.updateSections(res.json.sections);
          }
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-02-rec', cartData: res.json });
          }
          afterCartUpdate(res.json, true, 'rec-add');
        })
        .catch(function () {
          btn.removeAttribute('aria-busy');
          btn.innerHTML = original;
          if (row) {
            var err = document.createElement('div');
            err.className = 'c2-rec__error';
            err.textContent = t('recsAddError');
            row.parentNode.insertBefore(err, row.nextSibling);
            setTimeout(function () { if (err.parentNode) err.parentNode.removeChild(err); }, 4000);
          }
        });
    });
  }

  /* ------------------------------------------------ 10. cart update hub */

  var lastSignature = null;
  var lastSyncAt = 0;
  // FIX 1 — structural authority. A server-rendered section replacement carries
  // newer truth than any cart event from the mutation cycle that produced it,
  // so it advances a generation. Non-heal events are only trusted while the
  // generation we are synchronised with is current. Ordinals, never clocks.
  var structGen = 0;
  var healedGen = 0;

  function afterCartUpdate(cart, force, src) {
    if (!cart) return;
    // /cart/add.js answers with the added LINE ITEMS, not the cart: no
    // total_price, no item_count. Rendering from it prints "LE 0" and poisons
    // lastCart, so reject it here and pull the authoritative cart instead.
    if (typeof cart.total_price !== 'number' || typeof cart.item_count !== 'number') {
      scheduleHeal('invalid-payload');
      return;
    }
    // de-duplicate: cart.js fires BOTH cart:updated and PUB_SUB cartUpdate
    var fromHeal = !!src && src.indexOf('heal') === 0;
    if (!fromHeal && structGen > healedGen) {
      return;                                  // freshly rendered HTML outranks this event
    }
    if (fromHeal) healedGen = structGen;
    var sig = cart.item_count + ':' + cart.total_price + ':' + (cart.items || []).length;
    if (!force && sig === lastSignature) {
      return;
    }
    lastSignature = sig;
    lastCart = cart;
    lastSyncAt = Date.now();

    hideBanner();
    markTotalUpdating(false);
    syncCount(cart);
    syncFooter(cart, 'afterCartUpdate/' + (src || 'unknown'));
    syncRows();
    markAllRowsIdle();
    afterRemoval(cart);
  }

  function markAllRowsIdle() {
    $$('.cart-item', drawer()).forEach(function (row) { markRowLoading(row, false); });
  }

  /* --------------------------------------------------------- 11. wiring */

  function bindOnce() {
    var d = drawer(); if (!d || d.dataset.c2Bound === 'true') return;
    d.dataset.c2Bound = 'true';

    // capture-phase so the snapshot is taken before Ella drops the row
    d.addEventListener('click', interceptRemove, true);

    // quantity taps → localized loading on that row only
    d.addEventListener('click', function (e) {
      var q = e.target.closest('.quantity__button');
      if (!q) return;
      var row = q.closest('.cart-item');
      if (!row) return;
      if (q.classList.contains('quantity-minus') && q.classList.contains('c2-min')) {
        e.preventDefault(); e.stopPropagation(); return;   // never reach 0
      }
      hideToast();                                          // any cart mutation commits the removal
      clearRowError(row);
      markRowLoading(row, true);
      markTotalUpdating(true);
    }, true);

    // Undo toast interactions
    d.addEventListener('click', function (e) {
      if (e.target.closest('[data-c2-toast-action]')) {
        if (undoState && typeof undoState.onAction === 'function') undoState.onAction();
      }
    });
    var toast = toastEl();
    if (toast) {
      toast.addEventListener('mouseenter', pauseTimer);
      toast.addEventListener('mouseleave', resumeTimer);
      toast.addEventListener('focusin', pauseTimer);
      toast.addEventListener('focusout', resumeTimer);
    }

    // these all commit a pending Undo
    d.addEventListener('click', function (e) {
      if (e.target.closest('#CartDrawer-Checkout') ||
          e.target.closest('.c2-action--secondary') ||
          e.target.closest('#CartDrawer-Viewcart') ||
          e.target.closest('.drawer__close')) hideToast();
    });
    var overlay = $('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', hideToast);
    window.addEventListener('pagehide', hideToast);

    bindCoupon();
    bindShipping();
    bindRecommendations();
    syncRows();
    if (lastCart) syncFooter(lastCart, 'bindOnce');
  }

  // Ella replaces .section--main-cart on every cart response: re-apply after it.
  var healTimer = null;
  var healForced = false;   // FIX 2 — set by observer/structural, cleared when the timer fires
  function scheduleHeal(src) {
    if (src === 'observer/structural') healForced = true;
    if (healTimer) clearTimeout(healTimer);
    healTimer = setTimeout(function () {
      healTimer = null;
      var forced = healForced;
      healForced = false;
      // an event-driven sync already covered this mutation — but a structural
      // replacement always needs its authoritative /cart.js confirmation.
      if (!forced && Date.now() - lastSyncAt < 600) {
        return;
      }
      fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          afterCartUpdate(cart, true, 'heal/' + (src || 'unknown'));
        })
        .catch(function () { /* leave the last known state on screen */ });
    }, 350);
  }

  function observe() {
    var host = $('cart-drawer-items');
    if (!host || host.dataset.c2Observed === 'true') return;
    host.dataset.c2Observed = 'true';
    var mo = new MutationObserver(function (records) {
      var structural = records.some(function (r) { return r.type === 'childList' && r.addedNodes.length; });
      syncRows();
      markTotalUpdating(false);
      // A structural replacement IS freshly server-rendered HTML, so its footer
      // outranks anything we remember: never repaint it from lastCart. Drop the
      // remembered cart and let the heal re-establish it from /cart.js.
      if (!structural && lastCart) syncFooter(lastCart, 'observer/non-structural');
      if (structural) {
        lastCart = null;
        structGen += 1;
        scheduleHeal('observer/structural');
      }
    });
    mo.observe(host, { childList: true, subtree: true });
  }

  function boot() {
    bindOnce();
    observe();
    scheduleHeal();
    // seed from the rendered DOM; no extra /cart.js request on drawer open
  }

  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail) afterCartUpdate(e.detail, false, 'cart:updated');
    else afterCartUpdate(lastCart, true, 'cart:updated(no-detail)');
    bindOnce(); observe();
  });

  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();

  window.addEventListener('load', function () {
    boot();
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        if (payload && payload.cartData) afterCartUpdate(payload.cartData, false, 'pubsub:' + ((payload && payload.source) || 'unknown'));
        bindOnce(); observe();
      });
      window.subscribe(window.PUB_SUB_EVENTS.cartError, function () {
        markTotalUpdating(false);
        markAllRowsIdle();
      });
    }
  });

  // network failure on a cart mutation → banner + paused checkout (ER-03)
  document.addEventListener('c2:network-error', function () { showBanner(t('errNetwork')); });
  var reload = function (e) {
    if (e.target.closest && e.target.closest('[data-c2-reload]')) {
      hideBanner();
      renderSection();
    }
  };
  document.addEventListener('click', reload);
})();
